import { api } from "./api";

/**
 * Shared, de-duplicated reads of the public settings endpoints.
 *
 * `GET /homepage-config` was requested twice on every homepage load: `Home.jsx`
 * wants the hero image, title and CTA, and `Header.jsx` wants the announcement
 * bar text out of the same payload. Two independent `Promise.allSettled`
 * batches, same response, resolved twice — and the announcement ribbon is on
 * every page, so the second call happens on the product page and the basket
 * too, where nothing else needs it.
 *
 * Cached for the life of the tab: this is site configuration an editor changes
 * occasionally, not per-request data. In-flight requests are shared so two
 * components mounting in the same tick make one request rather than two, which
 * is the same trick `ProductCard` uses for variant lookups.
 */
const cache = new Map();
const inFlight = new Map();
/* 60s: short enough that an editor saving in admin sees the change on the
   storefront within a minute, long enough to still de-duplicate the burst of
   reads when several components mount together. */
const TTL_MS = 60 * 1000;

/* localStorage mirror so a reload can paint the last-known config
   synchronously — without it the homepage shows its bundled default hero for
   the ~1-2s the network call takes, then visibly swaps to the saved image. */
const LS_PREFIX = "adf:publicConfig:";

function persist(path, data) {
  try { localStorage.setItem(LS_PREFIX + path, JSON.stringify(data)); } catch { /* quota/private mode */ }
}

/** Last-known value for a path, read synchronously — for first render only. */
export function readCached(path) {
  const hit = cache.get(path);
  if (hit) return hit.data;
  try {
    const raw = localStorage.getItem(LS_PREFIX + path);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function read(path) {
  const hit = cache.get(path);
  if (hit && Date.now() - hit.at < TTL_MS) return Promise.resolve(hit.data);
  if (inFlight.has(path)) return inFlight.get(path);

  const request = api.get(path)
    .then((response) => {
      const data = response?.data || {};
      cache.set(path, { data, at: Date.now() });
      persist(path, data);
      return data;
    })
    .catch(() => ({}))     // callers already fall back to hardcoded copy
    .finally(() => { inFlight.delete(path); });

  inFlight.set(path, request);
  return request;
}

export function getHomepageConfig() {
  return read("/homepage-config");
}

/** The last saved homepage config, synchronously, for a flash-free first paint. */
export function getCachedHomepageConfig() {
  return readCached("/homepage-config");
}

export function getHeaderNavigation() {
  return read("/navigation?placement=header");
}

/**
 * The header menu already resolved into a tree: mega-menu columns nested under
 * their entry, catalogue-linked rows carrying the category's current name, and
 * anything hidden or out of schedule already dropped. `surface` decides whether
 * desktop-only or mobile-only entries are included.
 */
export function getHeaderNavigationTree(surface = "desktop") {
  return read(`/navigation/tree?placement=header&surface=${surface}`);
}

/** The last saved header tree, synchronously, for a flash-free first paint. */
export function getCachedHeaderNavigationTree(surface = "desktop") {
  return readCached(`/navigation/tree?placement=header&surface=${surface}`);
}

/** The taxonomy behind the header's category menu. Read once per tab. */
/** Footer navigation, as configured in admin. Same cache as the header's.
 *
 * `placement: footer` has been editable, and seeded, since the navigation table
 * shipped — and the footer never fetched it once, so an editor adding or
 * renaming a footer link changed nothing at all.
 */
export function getFooterNavigation() {
  return read("/navigation?placement=footer");
}

export function getCategories() {
  // Yalnız görünən + alt-ağacında published məhsul olan kateqoriyalar; köhnə
  // backend parametri tanımırsa sadəcə ignore edir (deploy sırası təhlükəsiz).
  return read("/categories?visible_only=true");
}

/**
 * Delivery and returns copy, as the admin panel has it.
 *
 * This endpoint has existed all along with no consumer: the product page got
 * these strings folded into its own payload, and every other surface — the
 * cart, order tracking, the /delivery and /returns pages — printed a hardcoded
 * module instead. The owner wrote a full returns policy in admin and the
 * returns page still said the terms were "agreed individually with the
 * concierge".
 */
export function getCatalogSettings() {
  return read("/catalog/settings");
}

/** For tests, and for an admin preview that needs to see its own edit. */
export function clearPublicConfigCache() {
  cache.clear();
  inFlight.clear();
}
